import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { resetCacheStamp } from "../../util/cacheManager";
import { CacheStoreKey } from "../../enum/cacheStore.enum";
import { handleNetworkResponse } from "../../util/helper";
import { Brand } from "../../types/Brand";
import { Persister } from "../../util/persister";

// Define a service using a base URL and expected endpoints
export const brandApi = createApi({
  reducerPath: "brandApi",
  tagTypes: [CacheStoreKey.metaBrandDBStamp],
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (build) => ({
    // mutation api
    createbrand: build.mutation({
      queryFn: async (brand: Omit<Brand, 'id'>) => {
        resetCacheStamp(CacheStoreKey.metaBrandDBStamp);
        const response = await axios.post(
          `/brand/create`,
          brand
        );
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaBrandDBStamp],
    }),

    updatebrand: build.mutation({
      queryFn: async (brand: Brand) => {
        resetCacheStamp(CacheStoreKey.metaBrandDBStamp);
        const response = await axios.post(
          `/brand/update`,
          brand
        );
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaBrandDBStamp],
    }),

    deletebrand: build.mutation({
      queryFn: async (args: { id: number }) => {
        resetCacheStamp(CacheStoreKey.metaBrandDBStamp);
        const response = await axios.post(`/brand/delete/${args.id}`);
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaBrandDBStamp],
    }),

    // read api
    readbrands: build.query({
      queryFn: async (args: {}) => {
        const response = await Persister.requestData(CacheStoreKey.metaBrandDBStamp, 'GET', `/brands`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.metaBrandDBStamp],
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useCreatebrandMutation,
  useDeletebrandMutation,
  useUpdatebrandMutation,
  useReadbrandsQuery,
  useLazyReadbrandsQuery
} = brandApi;

export type BrandApiType = typeof brandApi;